import Button from '@asphalt-react/button';
import { Edit, Calendar, Clipboard, Refresh, CloudUpload } from '@asphalt-react/iconpack';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Image, Shimmer } from 'react-shimmer';
import Permission from '../../authorization/components/Permission';
import actions from '../../authorization/models/actions';
import styles from './IllustrationViewer.module.css';
import IllustrationType from '../models/IllustrationType';
import configKeys from '../../common/models/config-keys';
import ConfigProvider from '../../common/components/ConfigProvider';
import BcsConfigurationModal from './BcsConfigurationModal';
import VariantTabControl from './VariantTabControl';
import routes from '../../common/models/route-constants';

function IllustrationViewer({
    combinedName,
    imgUrl,
    imgUrlDark,
    darkroomUrl,
    darkroomUrlDark,
    variantSync = true,
    showVariantToggle = true,
    illustrationName,
    eventsScheduled,
    onGenerateDarkroomUrl,
    isGeneratingDarkroomUrl,
    illustrationType = IllustrationType.DEFAULT,
    onEditButtonClick,
}) {
    const [copied, setCopied] = useState(false);
    const [isBcsModalOpen, setIsBcsModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState('light');

    const isDarkTab = showVariantToggle && selectedTab === 'dark';
    const hasDarkVariant = !variantSync && (imgUrlDark || darkroomUrlDark);

    const activeImgUrl = isDarkTab && hasDarkVariant ? imgUrlDark : imgUrl;
    const activeDarkroomUrl = isDarkTab && hasDarkVariant ? darkroomUrlDark : darkroomUrl;

    const illustrationTypeToIllustrationUpdateConfigMap = {};
    illustrationTypeToIllustrationUpdateConfigMap[IllustrationType.DEFAULT] =
        configKeys.IS_UPDATE_DEFAULT_ILLUSTRATION_ENABLED;
    illustrationTypeToIllustrationUpdateConfigMap[IllustrationType.REGIONAL] =
        configKeys.IS_UPDATE_REGIONAL_ILLUSTRATION_ENABLED;

    const handleCopy = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    return (
        <div className={styles.illustrationContainer}>
            <div className={styles.titleAndEditBtnContainer}>
                <p className={styles.illustrationName}>{illustrationName}</p>
                <ConfigProvider
                    configKey={
                        illustrationTypeToIllustrationUpdateConfigMap[
                        illustrationType
                        ]
                    }>
                    <div className={styles.editAndBcsButtonContainer}>
                        <Permission action={actions.UPDATE_ILLUSTRATION}>
                            <Button
                                nude
                                qualifier={<Edit />}
                                onClick={() => {
                                    onEditButtonClick();
                                }}>
                                Edit
                            </Button>
                        </Permission>

                        {illustrationType === IllustrationType.DEFAULT && !!darkroomUrl && (
                            <Permission action={actions.CONFIGURE_BCS}>
                                <span className={styles.bcsActionGroup}>
                                    <Button
                                        nude
                                        qualifier={<CloudUpload />}
                                        onClick={() => {
                                            setIsBcsModalOpen(true);
                                        }}>
                                        BCS
                                    </Button>
                                    <Link
                                        to={`/${routes.HELP}/${routes.BCS_FLOW_GUIDE}`}
                                        className={styles.bcsInfoLink}
                                        title="How BCS Config works"
                                        aria-label="How BCS Config works">
                                        i
                                    </Link>
                                </span>
                            </Permission>
                        )}
                    </div>
                </ConfigProvider>
            </div>

            {showVariantToggle && (
                <VariantTabControl
                    selectedTab={selectedTab}
                    onTabChange={(tab) => setSelectedTab(tab)}
                    className={styles.tabContainer}
                />
            )}

            <div className={`${styles.imageContainer} ${isDarkTab ? styles.imageContainerDark : ''}`}>
                {isDarkTab && variantSync && (
                    <span className={styles.mirrorsLightBadge}>⊙ Mirrors light</span>
                )}
                <Image
                    key={selectedTab}
                    src={activeImgUrl || imgUrl}
                    className={styles.image}
                    NativeImgProps={{ alt: illustrationName }}
                    fallback={<Shimmer width={360} height={240} />}
                />
            </div>

            {eventsScheduled >= 0 ? (
                <div className={styles.eventsScheduledContainer}>
                    <Calendar />
                    <p
                        className={
                            styles.eventsScheduledText
                        }>{`${eventsScheduled} events scheduled`}</p>
                </div>
            ) : null}

            {activeDarkroomUrl ? (
                <div className={styles.darkroomUrlContainer}>
                    <div className={styles.darkroomUrlHeader}>
                        <p className={styles.darkroomUrlLabel}>
                            {isDarkTab && variantSync ? 'Darkroom URL (same as light)' : 'Darkroom URL'}
                        </p>
                        <Button
                            nude
                            qualifier={<Clipboard />}
                            onClick={() => handleCopy(activeDarkroomUrl)}>
                            {copied ? 'Copied' : 'Copy'}
                        </Button>
                    </div>
                    <div className={styles.darkroomUrlValueWrapper}>
                        <p className={styles.darkroomUrlValue}>{activeDarkroomUrl}</p>
                        <span className={styles.darkroomUrlTooltip}>{activeDarkroomUrl}</span>
                    </div>
                </div>
            ) : onGenerateDarkroomUrl ? (
                <div className={styles.darkroomUrlContainer}>
                    <div className={styles.darkroomUrlHeader}>
                        <p className={styles.darkroomUrlLabel}>
                            {isDarkTab && variantSync ? 'Darkroom URL (same as light)' : 'Darkroom URL'}
                        </p>
                        <ConfigProvider
                            configKey={
                                illustrationTypeToIllustrationUpdateConfigMap[
                                illustrationType
                                ]
                            }>
                            <Permission action={actions.UPDATE_ILLUSTRATION}>
                                <Button
                                    nude
                                    qualifier={<Refresh />}
                                    disabled={isGeneratingDarkroomUrl}
                                    onClick={() => {
                                        onGenerateDarkroomUrl();
                                    }}>
                                    {isGeneratingDarkroomUrl ? 'Generating...' : 'Generate'}
                                </Button>
                            </Permission>
                        </ConfigProvider>
                    </div>
                    <p className={styles.darkroomUrlValue}>Not generated yet</p>
                </div>
            ) : null}

            {isBcsModalOpen && (
                <BcsConfigurationModal
                    combinedName={combinedName}
                    darkroomUrl={darkroomUrl}
                    darkroomUrlDark={variantSync ? darkroomUrl : (darkroomUrlDark ?? darkroomUrl)}
                    onClose={() => { setIsBcsModalOpen(false); }}
                />
            )}
        </div>
    );
}

export default IllustrationViewer;
